/**
 * iter84.4 (Phase 3) — Custom Tournament CSV Upload card.
 *
 * Lives inside the Data Feed tab on AdminPanel for custom tournaments whose
 * site admin has flipped `feed_csv_enabled` ON for the league.
 *
 * Flow:
 *   1) "View format" expands an inline tutorial with column reference +
 *      example row + the tournament's exact team/stage names.
 *   2) "Download template" pulls a per-tournament starter CSV from the
 *      backend (uses real team names + the tournament's first stage).
 *   3) "Upload CSV" sends the file to the backend. The backend validates
 *      the WHOLE file at once and either:
 *        a) succeeds → toast with imported/updated counts
 *        b) returns 400 with `{errors, row_errors}` → render an aggregate
 *           report grouped by row, listing every fixable issue at once.
 *
 * Match-level dedupe re-uses the same logic as the manual-submit path
 * (revert old stats, apply new ones) so leaderboards stay consistent
 * across re-uploads of the same file.
 */
import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, ChevronDown, ChevronUp, Info, X } from "lucide-react";
// iter87.180.9 — BrutalConfirm for the async destructive path (iOS
// Safari silently drops native confirms inside async handlers).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

export default function CsvUploadCard({ tournament, isLocked, onChanged }) {
    const [helpOpen, setHelpOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [errorReport, setErrorReport] = useState(null);
    // iter84.11 — Upload mode: "append" (add new games) or "replace"
    // (delete unplayed, keep played, repopulate).
    const [mode, setMode] = useState("append");
    const [uploadSummary, setUploadSummary] = useState(null);
    const [wizardDismissed, setWizardDismissed] = useState(false);
    const fileInputRef = useRef(null);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const downloadTemplate = async () => {
        try {
            const r = await api.csvUploadTemplate(tournament.id);
            const blob = new Blob([r.content], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = r.filename;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Template downloaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to download template");
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".csv")) {
            toast.error("Please upload a .csv file");
            e.target.value = "";
            return;
        }
        // iter84.11 — Replace mode requires confirmation since it deletes
        // all unplayed matches first.
        if (mode === "replace") {
            const ok = await brutalConfirm({
                title: "Replace schedule?",
                body: "• All UNPLAYED matches will be deleted.\n• Played/scored matches stay untouched.",
                confirmLabel: "Replace",
                destructive: true,
            });
            if (!ok) {
                e.target.value = "";
                return;
            }
        }
        setBusy(true);
        setErrorReport(null);
        setUploadSummary(null);
        try {
            const r = await api.csvUploadMatches(tournament.id, file, mode);
            setUploadSummary(r);
            toast.success(r.summary || `Added ${r.added} game(s)`);
            onChanged?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && (detail.row_errors || detail.errors)) {
                setErrorReport(detail);
                toast.error("CSV has validation errors — see the report below.");
            } else {
                toast.error(typeof detail === "string" ? detail : "CSV upload failed");
            }
        }
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const teams = tournament.teams || [];
    const stages = (tournament.stages || []).map(s => s.key);
    const hasNoMatches = (tournament.matches?.length || 0) === 0;

    return (
        <div className="brutal-card p-5" data-testid="csv-feed-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blaze" /> CSV Upload
                </h2>
                <button
                    type="button"
                    onClick={() => setHelpOpen(!helpOpen)}
                    data-testid="csv-help-toggle"
                    className="brutal-btn-ghost text-xs py-1.5"
                >
                    {helpOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {helpOpen ? "Hide format guide" : "View format guide"}
                </button>
            </div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                Upload your <strong className="text-white">schedule</strong> in bulk (teams + stage + optional kickoff). Scores are NOT in this file — you'll enter results manually in the Schedule tab as games finish.
                The whole file is validated at once — if anything is wrong, you'll get a single grouped error report so you can fix everything in one editing pass.
            </p>

            {helpOpen && (
                <div className="brutal-card-light p-4 mt-4 space-y-3 text-xs text-zinc-300 leading-relaxed" data-testid="csv-format-guide">
                    <div className="flex items-center gap-2 text-cyber font-display font-extrabold text-sm uppercase tracking-tight">
                        <Info className="w-4 h-4" /> CSV Format Guide
                    </div>
                    <div>
                        <p className="text-zinc-400 mb-1.5">Required columns (header row, exact spelling, lowercase):</p>
                        <code className="block p-2 bg-ink border border-[#333] text-cyber font-mono text-[11px] break-all">
                            home_team,away_team,stage,group,kickoff_at
                        </code>
                    </div>
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li><strong className="text-white">home_team / away_team</strong> — must match a team name in this tournament exactly (case-insensitive). Unknown teams cause row errors.</li>
                        <li><strong className="text-white">stage</strong> — one of: <span className="font-mono text-cyber">{stages.join(", ") || "(no stages configured)"}</span></li>
                        <li><strong className="text-white">group</strong> — required only when <span className="font-mono">stage = group</span>. Leave blank for knockout/regular.</li>
                        <li><strong className="text-white">kickoff_at</strong> — optional ISO datetime (e.g. <span className="font-mono">2026-07-04T18:30:00Z</span>). Leave blank if unknown.</li>
                    </ul>
                    <div>
                        <p className="text-zinc-400 mb-1.5">Teams available in this tournament:</p>
                        <div className="flex flex-wrap gap-1">
                            {teams.slice(0, 24).map(t => (
                                <span key={t.id} className="pill text-[10px] border-[#333] bg-ink text-zinc-300">{t.name}</span>
                            ))}
                            {teams.length > 24 && (
                                <span className="pill text-[10px] border-[#333] bg-ink text-zinc-500">+{teams.length - 24} more</span>
                            )}
                        </div>
                    </div>
                    <div className="pt-2 border-t border-[#222]">
                        <p className="text-zinc-400 mb-1.5">Example row (group stage):</p>
                        <code className="block p-2 bg-ink border border-[#333] text-zinc-300 font-mono text-[11px] break-all">
                            {teams[0]?.name || "Brazil"},{teams[1]?.name || "Croatia"},group,A,
                        </code>
                    </div>
                    <div className="pt-2 border-t border-[#222]">
                        <strong className="text-volt">Schedule-only:</strong> every row is inserted as an unplayed match (<span className="font-mono">played=false</span>). Score games in the Schedule tab once they finish — the leaderboard recomputes automatically.
                    </div>
                </div>
            )}

            {/* iter84.11 — First-time setup wizard. Shows when the
                tournament has no matches yet AND the admin hasn't
                dismissed it. 3-step guide. */}
            {hasNoMatches && !wizardDismissed && (
                <div className="brutal-card-light p-4 mt-4 border-cyber/60 space-y-2" data-testid="csv-firsttime-wizard">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="font-display font-extrabold text-sm uppercase text-cyber tracking-tight">First time? Quick setup</h3>
                        <button type="button" onClick={() => setWizardDismissed(true)} data-testid="csv-firsttime-dismiss" className="text-zinc-500 hover:text-white text-xs">Dismiss</button>
                    </div>
                    <ol className="list-decimal pl-5 space-y-1 text-xs text-zinc-300 leading-relaxed">
                        <li><strong className="text-white">Add your teams</strong> in the <em>Teams</em> tab (their names must match what you'll put in the CSV).</li>
                        <li><strong className="text-white">Upload your schedule CSV here</strong> using the buttons below. Scores are NOT in the CSV — this is schedule-only.</li>
                        <li><strong className="text-white">Score games in the Schedule tab</strong> as they finish. The leaderboard updates automatically.</li>
                    </ol>
                </div>
            )}

            {/* iter84.11 — Upload mode selector: append vs replace. */}
            <div className="flex flex-wrap gap-2 mt-4">
                <button
                    type="button"
                    onClick={() => setMode("append")}
                    data-testid="csv-mode-append-btn"
                    className={`brutal-btn text-xs py-2 ${mode === "append" ? "bg-cyber text-black border-black shadow-brutal" : "bg-transparent text-zinc-400 border-[#333] shadow-none"}`}
                >
                    Add new games
                </button>
                <button
                    type="button"
                    onClick={() => setMode("replace")}
                    data-testid="csv-mode-replace-btn"
                    className={`brutal-btn text-xs py-2 ${mode === "replace" ? "bg-heat text-white border-black shadow-brutal" : "bg-transparent text-zinc-400 border-[#333] shadow-none"}`}
                >
                    Replace whole schedule
                </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                {mode === "append"
                    ? "Adds every CSV row as a NEW scheduled game. Rows matching an existing schedule row get flagged in the summary (they'll still be added, since teams may play each other multiple times)."
                    : "Wipes ALL unplayed matches and replaces them with your CSV. Played/scored matches are preserved — you'll see a count in the summary."}
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
                <button
                    type="button"
                    onClick={downloadTemplate}
                    disabled={busy || isLocked}
                    data-testid="csv-download-template-btn"
                    className="brutal-btn-secondary text-xs py-2 disabled:opacity-40"
                >
                    <Download className="w-3.5 h-3.5" /> Download template
                </button>
                <label
                    className={`brutal-btn text-xs py-2 cursor-pointer bg-blaze text-black border-black shadow-brutal ${busy || isLocked ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="csv-upload-label"
                >
                    <Upload className="w-3.5 h-3.5" />
                    {busy ? "Validating…" : "Upload CSV"}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        disabled={busy || isLocked}
                        onChange={handleUpload}
                        data-testid="csv-upload-input"
                    />
                </label>
            </div>

            {/* iter84.11 — Upload summary panel. Shows after a successful
                upload with counts + duplicates-in-DB report. */}
            {uploadSummary && (
                <div className="mt-4 brutal-card-light p-4 border-cyber/60 space-y-2" data-testid="csv-upload-summary">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="font-display font-extrabold text-sm uppercase text-cyber tracking-tight">Upload complete</h3>
                        <button type="button" onClick={() => setUploadSummary(null)} className="text-zinc-500 hover:text-white" aria-label="Dismiss">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <p className="text-xs text-zinc-300 leading-relaxed">{uploadSummary.summary}</p>
                    {Array.isArray(uploadSummary.duplicates_in_db) && uploadSummary.duplicates_in_db.length > 0 && (
                        <div className="space-y-1 max-h-48 overflow-y-auto pt-2 border-t border-[#222]" data-testid="csv-duplicates-list">
                            <p className="text-[10px] uppercase font-bold text-blaze tracking-widest">Heads up — duplicates added as extra games:</p>
                            {uploadSummary.duplicates_in_db.map((d, i) => (
                                <div key={i} className="text-[11px] text-zinc-300 pl-2 border-l-2 border-blaze/60">
                                    Row {d.row}: <strong>{d.teams}</strong> ({d.stage}) — {d.note}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {errorReport && (
                <div className="mt-4 brutal-card-light p-4 border-heat/60" data-testid="csv-error-report">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-display font-extrabold text-sm uppercase text-heat tracking-tight">
                            Validation errors
                        </h3>
                        <button
                            type="button"
                            onClick={() => setErrorReport(null)}
                            data-testid="csv-error-dismiss"
                            className="text-zinc-500 hover:text-white"
                            aria-label="Dismiss"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    {errorReport.summary && (
                        <p className="text-xs text-zinc-300 mb-3 leading-relaxed">{errorReport.summary}</p>
                    )}
                    {Array.isArray(errorReport.errors) && errorReport.errors.length > 0 && (
                        <ul className="list-disc pl-5 space-y-1 text-xs text-heat mb-3">
                            {errorReport.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    )}
                    {Array.isArray(errorReport.row_errors) && errorReport.row_errors.length > 0 && (
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="csv-row-errors">
                            {errorReport.row_errors.map((re, i) => (
                                <div key={i} className="border border-[#333] rounded p-2 bg-ink/60" data-testid={`csv-row-error-${re.row}`}>
                                    <div className="text-[10px] uppercase font-bold text-heat tracking-widest mb-1">Row {re.row}</div>
                                    <ul className="list-disc pl-5 space-y-0.5 text-[11px] text-zinc-200">
                                        {re.issues.map((iss, j) => <li key={j}>{iss}</li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                        Fix all of the above in your CSV and re-upload. Nothing was imported — the file is rejected as a whole.
                    </p>
                </div>
            )}
            {confirmHost}
        </div>
    );
}
